import _ from 'lodash';
import { EVERYONE_USERGROUP } from '@/administration/administration.constants';
import { getVantageDataModelOverride } from '@/services/vantageDataModelOverride.utilities';
import { sqSystemApi } from '@seeqdev/api/endpoint/SystemApi';
import { sqUserGroupsApi } from '@seeqdev/api/endpoint/UserGroupsApi';
import type { Timezone } from '@/datetime/timezone.service';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { PriorityV1 } from '@seeqdev/api/model/PriorityV1';
import type {
  ConditionMonitorMetadataOutputV1,
  ServerSpecOutputV1,
  SupportedUnitsOutputV1,
} from '@seeqdev/api/model/models';

interface ConfigData {
  configurationOptions: Record<string, unknown>;
  timeZone?: Timezone;
  defaultMaxInterpolation?: FrontendDuration;
  serverSpecs?: Array<ServerSpecOutputV1>;
  defaultMaxCapsuleDuration?: FrontendDuration;
  atUserLimit?: boolean;
  priorities?: PriorityV1[];
  isDataLabAvailable?: boolean;
  seeqGptEnabled?: boolean;
}

let configurationData: ConfigData | undefined;
type SupportedUnitsPromise = Promise<{ [key: string]: string[] } | undefined>;
let supportedUnits: SupportedUnitsPromise;
let everyoneGroupEnabled: boolean | undefined = true;

export function setConfigurationData(data: ConfigData | undefined) {
  configurationData = data;
}

export function isConfigurationLoaded() {
  try {
    return configurationData !== undefined;
  } catch {
    return false;
  }
}

function getConfigurationOption(path: string): any {
  if (!_.has(serverConfiguration().configurationOptions, path)) {
    return undefined;
  }

  return serverConfiguration().configurationOptions[path];
}

// Because we need the user groups API, this has to be done after login
export function fetchEveryoneDisabled() {
  return sqUserGroupsApi.getUserGroups({ nameSearch: EVERYONE_USERGROUP }).then(({ data }) => {
    const everyoneGroup = _.find(data.items, { name: EVERYONE_USERGROUP });
    everyoneGroupEnabled = !_.isUndefined(everyoneGroup) && everyoneGroup.isEnabled;
  });
}

/**
 * All the getter methods in this file should return data that is immutable, because these outputs are often used as
 * inputs to useEffect dependency arrays, or passed as props into a React component
 */

export function newHeaderExperienceEnabled() {
  return getConfigurationOption('Features/UX/NewHeader/Enabled');
}

export function assetGroupEditorEnabled() {
  return getConfigurationOption('Features/AssetGroupEditor/Enabled');
}

export function seeqDirectoryEnabled() {
  return getConfigurationOption('Authentication/SeeqDirectory/Enabled');
}

export function getPasswordMinLength() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordMinLength');
}

export function getPasswordUppercaseRequired() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordComplexity/RequireUppercase');
}

export function getPasswordLowercaseRequired() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordComplexity/RequireLowercase');
}

export function getPasswordDigitRequired() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordComplexity/RequireDigit');
}

export function getPasswordSpecialCharacterRequired() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordComplexity/RequireSpecialCharacter');
}

// Memoized since it is not expected to change, but only needed in some tools
export function getSupportedUnits(): SupportedUnitsPromise {
  if (_.isUndefined(supportedUnits)) {
    supportedUnits = sqSystemApi.getSupportedUnits().then(({ data }: { data: SupportedUnitsOutputV1 }) => data.units);
  }

  return supportedUnits;
}

function serverConfiguration(): Readonly<ConfigData> {
  if (_.isUndefined(configurationData)) {
    throw new Error('Configuration must be fetched before being accessed');
  }

  return configurationData;
}

export function defaultMaxInterpolation(): FrontendDuration | undefined {
  return serverConfiguration().defaultMaxInterpolation;
}

export function priorityColors(): PriorityV1[] | undefined {
  return serverConfiguration().priorities;
}

export function serverTimezone(): Timezone | undefined {
  return configurationData?.timeZone;
}

export function isDataLabAvailable() {
  return configurationData?.isDataLabAvailable;
}

export function seeqVersion(): string | undefined {
  return getConfigurationOption('Installation/Version');
}

export function seeqReleaseIdentifier(): number | undefined {
  const releaseIdentifier = getConfigurationOption('Installation/ReleaseIdentifier');
  const releaseIdentifierMatch = releaseIdentifier?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (releaseIdentifierMatch) {
    return _.toNumber(`${releaseIdentifierMatch[1]}${releaseIdentifierMatch[2]}${releaseIdentifierMatch[3]}`);
  }
}

export function seeqFullReleaseIdentifier(): string | undefined {
  return getConfigurationOption('Installation/ReleaseIdentifier');
}

export function maxScatterPlotSamples() {
  return getConfigurationOption('Features/ScatterPlot/MaxPoints');
}

export function serverSpecs() {
  return serverConfiguration().serverSpecs;
}

export function restrictLogs() {
  return getConfigurationOption('Features/RestrictLogs/Enabled');
}

export function registrationEnabled() {
  return getConfigurationOption('Features/UserRegistration/Enabled');
}

export function authDefaultProviderId() {
  return getConfigurationOption('Authentication/DefaultProviderId');
}

export function authAutoLogin() {
  return getConfigurationOption('Authentication/AutoLogin');
}

export function isTelemetryEnabled() {
  return getConfigurationOption('Features/Telemetry/Enabled');
}

export function isWebserverTelemetryEnabled() {
  return getConfigurationOption('Features/Telemetry/Webserver/Enabled');
}

export function isTelemetryAnonymized() {
  return getConfigurationOption('Features/Telemetry/Anonymized');
}

export function adminContactName(): string {
  return getConfigurationOption('Installation/AdminContact/Name');
}

export function adminContactEmail(): string {
  return getConfigurationOption('Installation/AdminContact/Email');
}

export function isApprovalFlowsEnabled(): boolean {
  return getConfigurationOption('Features/ApprovalFlows/Enabled');
}

export function defaultNumberFormat() {
  return getConfigurationOption('Features/NumberFormat');
}

export function defaultStringFormat() {
  return getConfigurationOption('Features/StringFormat');
}

export function addOnToolsEnabled() {
  return getConfigurationOption('Features/AddOnTools/Enabled');
}

export function systemMessage() {
  return getConfigurationOption('Features/Messages/SystemMessage');
}

export function auditingEnabled() {
  return getConfigurationOption('Features/Auditing/Enabled');
}

export function auditingAllUsersCanRead() {
  return getConfigurationOption('Features/Auditing/AllUsersCanRead');
}

export function dayFirstDefault() {
  return getConfigurationOption('Features/Datafiles/DayFirstDefault') as boolean;
}

export function displaysEnabled() {
  return getConfigurationOption('Features/Displays/Enabled');
}

export function delimiterDefault() {
  return getConfigurationOption('Features/Datafiles/DelimiterDefault').toLowerCase();
}

export function officeHoursEnabled() {
  return getConfigurationOption('Features/HomeScreen/OfficeHours/Enabled');
}

export function customSidebar() {
  return getConfigurationOption('Features/HomeScreen/CustomSidebar');
}

export function pluginsEnabled() {
  return getConfigurationOption('Features/Plugins/Enabled');
}

export function agentBuilderEnabled() {
  return getConfigurationOption('Features/AgentBuilder/Enabled');
}

export function agentSkillsEnabled() {
  return getConfigurationOption('Features/AgentSkills/Enabled');
}

export function knowledgeManagementEnabled() {
  return getConfigurationOption('Features/KnowledgeManagement/Enabled');
}

export function releaseNotesEnabled() {
  return getConfigurationOption('Features/ReleaseNotes/Enabled');
}

export function hardwareWarningsEnabled() {
  return getConfigurationOption('Features/HardwareWarnings/Enabled');
}

export function aiAssistantEnabled() {
  return configurationData?.seeqGptEnabled;
}

export function genAIEnabled() {
  return getConfigurationOption('Features/GenAI/Enabled');
}

export function genAIAgents(): string {
  const agents = getConfigurationOption('Features/GenAI/Agents');

  // CRAB-47448: We renamed workbench --> actions & CRAB-55550: We renamed tome --> 'document' user-facing but still tome in agent functions
  return agents.replace('workbench', 'actions').replace('document', 'tome');
}

export function genAIImageUploadsEnabled(): boolean {
  return getConfigurationOption('Features/GenAI/ImageUploadsEnabled');
}

export function isLicenseWarningHidden() {
  return getConfigurationOption('Features/LicenseWarning/Hidden');
}

export function isEveryoneGroupEnabled() {
  return everyoneGroupEnabled;
}

export function isAdvancedDateRangeSwapEnabled() {
  return getConfigurationOption('Features/AdvancedDateRangeSwap/Enabled');
}

export function isScalingTableEditorEnabled() {
  return getConfigurationOption('Features/ScalingTableEditor/Enabled');
}

export function isItemTableEditorEnabled() {
  return getConfigurationOption('Features/ItemTableEditor/Enabled');
}

export function isScalingTableCapabilityVisible() {
  return getConfigurationOption('Features/ScalingTableCapability/Enabled');
}

export function isScalingTableAssetTreeCreationEnabled() {
  return getConfigurationOption('Features/ScalingTableAssetTreeCreation/Enabled');
}

export function isSimpleTableTreemapEnabled() {
  return getConfigurationOption('Features/SimpleTableTreeMap/Enabled');
}

export function isTemplatesEnabled() {
  return getConfigurationOption('Features/Templates/Enabled');
}

export function isStatisticsEnabled() {
  return getConfigurationOption('Features/Statistics/Enabled');
}

export function atUserLimit() {
  return configurationData?.atUserLimit || false;
}

export function isSeeqPreviewEnabled() {
  return getConfigurationOption('Features/SeeqPreview/Enabled');
}

export function isLearnModeEnabled() {
  return getConfigurationOption('Features/SeeqLearn/Enabled');
}

export function isMixpanelEnabled() {
  return getConfigurationOption('Features/Mixpanel/Enabled');
}

export function isAnnouncementsEnabled() {
  return getConfigurationOption('Features/Announcements/Enabled');
}

export function isVantageChartsEnabled() {
  return getConfigurationOption('Features/Vantage/Charts/Enabled');
}

export function isVantageRunLogEnabled() {
  return getConfigurationOption('Features/Vantage/RunLog/Enabled');
}

export function isVantageContextOnArbitraryTimeRangeEnabled() {
  return getConfigurationOption('Features/Vantage/ContextOnArbitraryTimeRange/Enabled');
}

export function isVantageNameBasedAssetContextEnabled() {
  return getConfigurationOption('Features/Vantage/NameBasedAssetContext/Enabled');
}

export function isVantageServerSideRowModelEnabled(): boolean {
  // CRAB-60996, remove when confident in SSRM
  const override = getVantageDataModelOverride();
  if (override !== undefined) {
    return override;
  }
  return Boolean(getConfigurationOption('Features/Vantage/ServerSideRowModel/Enabled'));
}

export function getVantageStringConcatLimit(): number {
  return getConfigurationOption('Features/Vantage/ServerSideRowModel/StringConcatLimit');
}

export function isVantageCommentEditingEnabled() {
  return getConfigurationOption('Features/Vantage/CommentEditing/Enabled');
}

export function isVantageMonitoredConditionsManagementEnabled() {
  return getConfigurationOption('Features/Vantage/MonitoredConditionsManagement/Enabled');
}

export function isVantageReasonCodesManagementEnabled(): boolean {
  return Boolean(getConfigurationOption('Features/Vantage/ReasonCodesManagement/Enabled'));
}

export const workbenchTextContextEnrichment = (): boolean =>
  getConfigurationOption('Features/Workbench/TextContextEnrichment/Enabled') === true;

export function isLockingAndVersioningEnabled() {
  return getConfigurationOption('Features/LockingAndVersioning/Enabled');
}

export function isLockingAndVersioningOrganizerEnabled() {
  return getConfigurationOption('Features/LockingAndVersioningOrganizer/Enabled');
}

export function getDefaultMaxInterpolation(): FrontendDuration | undefined {
  return serverConfiguration().defaultMaxInterpolation;
}

export function getDefaultMaxCapsuleDuration(): FrontendDuration | undefined {
  return serverConfiguration().defaultMaxCapsuleDuration;
}

export function isConditionMonitorNotificationsEnabled(): boolean {
  return getConfigurationOption('Features/Notifications/ConditionMonitors/Enabled');
}

export function isEmailerServiceEnabled(): boolean {
  return getConfigurationOption('Features/EmailerService/Enabled');
}

export function getConditionMonitorSchedule(): string {
  return getConfigurationOption('Features/Notifications/ConditionMonitors/Schedule');
}

export function getConditionMonitorScheduleDescription(): string {
  return getConfigurationOption('Features/Notifications/ConditionMonitors/ScheduleDescription');
}

/**
 * Returns the schedule description to display for a condition monitor.
 * Falls back to the admin default when the monitor has no cron schedule.
 */
export function getConditionMonitorScheduleDisplayFromMetadata(
  metadata: Pick<ConditionMonitorMetadataOutputV1, 'cronSchedule' | 'cronScheduleDescription'>,
): string {
  return _.isEmpty(metadata.cronSchedule)
    ? (getConditionMonitorScheduleDescription() ?? '')
    : (metadata.cronScheduleDescription ?? '');
}

export function getConditionMonitorMinimumInterval(): number {
  return getConfigurationOption('Features/Notifications/ConditionMonitors/MinimumInterval');
}

export function isConditionMonitorSearchPreviewEnabled(): boolean {
  return getConfigurationOption('Features/Notifications/ConditionMonitors/SearchPreview/Enabled');
}

export function getItemFinderSchedule(): string {
  return getConfigurationOption('Features/ItemFinders/Schedule');
}

export function getItemFinderScheduleDescription(): string {
  return getConfigurationOption('Features/ItemFinders/ScheduleDescription');
}

export function isStoredTableItemFinderEnabled(): boolean {
  return getConfigurationOption('Features/ItemFinders/StoredTableItemFinder/Enabled');
}

export function isTableCsvImportEnabled(): boolean {
  return getConfigurationOption('Features/ItemFinders/CsvImport/Enabled');
}

export function getLoginMessage(): string {
  return getConfigurationOption('Features/Login/InfoMessage');
}

export function isVantageEnabled() {
  return getConfigurationOption('Features/Vantage/Enabled');
}

export function getODataDefaultProtocol() {
  return getConfigurationOption('Features/OData/DefaultProtocol');
}

export function isODataLegacyEnabled() {
  return getConfigurationOption('Features/OData/Legacy/Enabled');
}

export function isImpactReportsToolEnabled() {
  return getConfigurationOption('Features/ImpactReports/Enabled');
}

export function isEnterpriseInsightsEnabled() {
  return getConfigurationOption('Features/EnterpriseInsights/Enabled');
}

export function isSelfServicePasswordResetEnabled() {
  return getConfigurationOption('Authentication/SeeqDirectory/SelfServicePasswordReset');
}

export function isEnforcePasswordAgeEnabled() {
  return getConfigurationOption('Authentication/SeeqDirectory/EnforcePasswordAge');
}

export function getMaxPasswordAge() {
  return getConfigurationOption('Authentication/SeeqDirectory/MaxPasswordAge');
}

export function getPasswordExpirationGracePeriod() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordExpirationGracePeriod');
}

export function getPasswordExpirationWarningPeriod() {
  return getConfigurationOption('Authentication/SeeqDirectory/PasswordExpirationWarningPeriod');
}

export function isONNXToolEnabled() {
  return getConfigurationOption('Features/MLTools/ONNX/Enabled');
}

export function isMLToolsEnabled() {
  return getConfigurationOption('Features/MLTools/MLService/Enabled');
}

export function isLegacyMLToolsEnabled() {
  return getConfigurationOption('Features/MLTools/Legacy/Enabled');
}

export function getGenAIBannerText(): string {
  return getConfigurationOption('Features/GenAI/BannerText') || '';
}

export function getResponseDisclaimer(): string {
  return getConfigurationOption('Features/GenAI/ResponseDisclaimer') || '';
}

export function getAgentQCustomPrompts(): string {
  return getConfigurationOption('Features/GenAI/AgentQCustomPrompts') || '';
}

export function isNewHomeScreen2025Enabled() {
  return getConfigurationOption('Features/HomeScreen/NewHomeScreen2025/Enabled');
}

export function isHomeScreenGraphSocialDiscoveryEnabled() {
  return getConfigurationOption('Features/Miner/HomeScreenGraphSocialDiscovery');
}

export function isMeilisearchSearchEnabled() {
  return getConfigurationOption('Features/Search/Meilisearch/Enabled');
}

export function isNewHomeScreenWithMeilisearchEnabled() {
  return isNewHomeScreen2025Enabled() && isMeilisearchSearchEnabled();
}

export function isAccessKeyCreationEnabled() {
  return getConfigurationOption('Authentication/AccessKey/Creation/Enabled');
}

export function isInternalLinksTargetNewTabEnabled() {
  return getConfigurationOption('Features/InternalLinksTargetNewTab/Enabled');
}

export function isEarlyAccessCapabilityEnabled() {
  return getConfigurationOption('Features/EarlyAccessCapability/Enabled');
}

export function isDLFSwitchingEnabled() {
  return getConfigurationOption('Features/DataLab/DLFSwitching/Enabled');
}

export function isExternalOrganizationTabEnabled() {
  return getConfigurationOption('Features/Administration/ExternalOrganizations/Enabled');
}
